import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { getPlural } from 'lib/string/pluralize'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import useEmployeeOrders from 'businessTraveller/hooks/useEmployeeOrders'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import BusinessTravellerAccountGuard from '../BusinessTravellerAccountGuard'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { authLogout } from 'actions/AuthActions'
import BusinessTravellerAccountListItemFailedAccess from './BusinessTravellerAccountListItemFailedAccess'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import { clearBusinessTravellerSelectedTraveller } from 'businessTraveller/storage/businessTravellerStorage'
import GeoContext from 'contexts/geoContext'
import useBusinessTravellerTripRequestApprovals from 'businessTraveller/hooks/useBusinessTravellerTripRequestApprovals'
import useBusinessTravellerTripRequests from 'businessTraveller/hooks/useBusinessTravellerTripRequests'
import BodyText from 'LuxKit/Typography/BodyText'
import { isBusinessTravellerTripRequestApprover } from 'businessTraveller/selectors/isBusinessTravellerTripRequestApprover'
import { BUSINESS_TRAVELLER_MY_APPROVALS_PENDING_ROUTE, BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_ACTIVE_ROUTE } from 'components/App/Routes/routeConstants'

interface MappedStateProps {
  currentBusinessAccount?: App.BusinessTraveller.CurrentBusinessAccount
  currentPersonalAccount: App.AuthAccount
}

interface Props {
  onListItemClick?: () => void
}

function BusinessTravellerAccountMenuContent(props: Props & MappedStateProps) {
  const {
    currentBusinessAccount,
    onListItemClick,
  } = props
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)

  const employeeId = currentBusinessAccount?.employee?.id
  const isBusinessTravellerApprover = useAppSelector(isBusinessTravellerTripRequestApprover)

  const { upcoming } = useBusinessTravellerTripRequests({ currentRegionCode, isEnabled: !!employeeId })
  const { pending } = useBusinessTravellerTripRequestApprovals({ currentRegionCode, isEnabled: !!employeeId })

  const { total: numberOfUpcomingOrders } = useEmployeeOrders({
    timeframe: 'upcoming',
    page: 0,
    limit: 1,
  })

  useEffect(() => {
    dispatch(fetchAccountBusiness())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const myOrdersLabel = useMemo<string>(() => {
    let text = `My ${getPlural(config.brandConfig.purchaseSynonym, 2)}`

    if (numberOfUpcomingOrders) {
      text += ` (${numberOfUpcomingOrders} upcoming)`
    }

    return text
  }, [numberOfUpcomingOrders])

  const modalState = useModalElementContext<'item-clicked'>()

  const handleItemClick = useCallback(() => {
    modalState?.resolve('item-clicked')
    onListItemClick?.()
  }, [modalState, onListItemClick])

  const logOut = useCallback(() => {
    // stolen from `AccountLandingPage.tsx`
    config.GOOGLE_ONE_TAP_LOGIN && window.google?.accounts?.id.disableAutoSelect()
    clearBusinessTravellerSelectedTraveller()
    dispatch(authLogout())
    handleItemClick()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Group direction="vertical" gap={8}>
    <BusinessTravellerAccountGuard
      loading={<ListItemLoadingSkeleton startIcon={<LineBuildingIcon />} subtitle="100%" />}
      fallback={<BusinessTravellerAccountListItemFailedAccess />}
    >
      <ClickableListItem
        href={config.businessTraveller.businessPortalHref}
        target="_blank"
        startIcon={<LineBuildingIcon />}
        title={`Business · ${currentBusinessAccount?.business?.name}`}
        subtitle={currentBusinessAccount?.employee?.email}
      />
    </BusinessTravellerAccountGuard>
    <Divider kind="primary" />
    <div>
      <BusinessTravellerAccountGuard
        employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
        loading={<ListItemLoadingSkeleton startIcon={<LineBuildingIcon />} subtitle="100%" />}
      >
        <ClickableListItem
          href={config.businessTraveller.businessAdminPortalHref}
          target="_blank"
          title="Admin Dashboard"
        />
      </BusinessTravellerAccountGuard>
      <ClickableListItem
        to="/account/my-bookings"
        title={myOrdersLabel}
        titleFormat="titlecase"
        onClick={handleItemClick}
      />
      <ClickableListItem
        to={BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_ACTIVE_ROUTE}
        title={<BodyText variant="large">My Trip Requests ({upcoming?.length ?? 0})</BodyText>}
        onClick={handleItemClick}
      />
      {isBusinessTravellerApprover && <ClickableListItem
        to={BUSINESS_TRAVELLER_MY_APPROVALS_PENDING_ROUTE}
        title={<BodyText variant="large">My Approvals ({pending?.length ?? 0})</BodyText>}
        onClick={handleItemClick}
      />}
      <ClickableListItem
        to="/account"
        title="Account"
        onClick={handleItemClick}
      />
    </div>
    <Divider kind="primary" />
    <ClickableListItem type="button" title="Log out" onClick={logOut} />
  </Group>
}

export default connect<MappedStateProps, undefined, {}, App.State>((state) => {
  return {
    currentBusinessAccount: state.businessTraveller.currentBusinessAccount,
    currentPersonalAccount: state.auth.account,
  }
})(BusinessTravellerAccountMenuContent)
